import { useState } from 'react';
import { Building2, Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { Input, Menu, MenuItem, MenuSeparator, Tooltip } from '@strivelabs/ui-core/v2';
import { useSidebar } from '@strivelabs/ui-blocks/v2';
// Deep import rather than the package root: `cn` is a utility, and the root
// barrel would pull in the whole v1 component set with it.
import { cn } from '@strivelabs/ui-core/lib/utils';
import { AddAccountModal } from '@/components/auth/AddAccountModal';
import { useOrgSwitcher } from '../hooks/useOrgSwitcher';

/** Organisation avatar, falling back to a generic mark. */
function OrgMark({ imageUrl }: { imageUrl?: string }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className="size-5 shrink-0 rounded-v2-sm object-cover outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
      />
    );
  }
  return (
    <span
      aria-hidden
      className="grid size-5 shrink-0 place-items-center rounded-v2-sm bg-v2-sidebar-accent"
    >
      <Building2 size={12} className="text-v2-sidebar-foreground" />
    </span>
  );
}

/**
 * Switches the active organisation.
 *
 * Renders as a static row when the user belongs to a single organisation, since
 * a menu with one item and no other action is just noise. Opens downward — it
 * sits at the top of the sidebar.
 */
export function OrgSwitcher() {
  const [open, setOpen] = useState(false);
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { collapsed } = useSidebar();
  const { isReady, organization, memberships, hasMultipleOrgs, canCreateOrg, switchOrg } =
    useOrgSwitcher();

  const visibleMemberships = query.trim()
    ? memberships.filter(entry =>
        entry.organization.name.toLowerCase().includes(query.trim().toLowerCase()),
      )
    : memberships;

  if (!isReady) {
    return (
      <div
        aria-hidden
        className="h-8 w-full animate-pulse rounded-v2-md bg-v2-sidebar-accent motion-reduce:animate-none"
      />
    );
  }

  const name = organization?.name ?? 'No organisation';
  // A filled, hairline-bordered row rather than bare text: it reads as the
  // control it is, and separates the org from the nav tree below it.
  // In the rail there is room for the mark alone, so the row becomes a square
  // and the name moves to a tooltip — the same treatment the account card gets.
  const rowClass = cn(
    'flex h-9 items-center gap-2 rounded-v2-lg border border-v2-sidebar-border bg-v2-background text-left text-v2-base text-v2-sidebar-foreground shadow-sm',
    collapsed ? 'w-9 justify-center' : 'w-full px-2',
  );

  if (!hasMultipleOrgs && !canCreateOrg) {
    return (
      <div className={rowClass}>
        <OrgMark imageUrl={organization?.imageUrl} />
        <span className="truncate">{name}</span>
      </div>
    );
  }

  const triggerButton = (
    <button
      type="button"
      aria-label={`Current organisation: ${name}. Switch organisation`}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        rowClass,
        'transition-[background-color,transform] duration-150 hover:bg-v2-sidebar-accent active:scale-[0.96]',
        'motion-reduce:transition-none motion-reduce:active:scale-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-sidebar-ring',
        open && 'bg-v2-sidebar-accent',
      )}
    >
      <OrgMark imageUrl={organization?.imageUrl} />
      {collapsed ? null : (
        <>
          <span className="truncate">{name}</span>
          <ChevronsUpDown size={14} className="ml-auto shrink-0 opacity-50" />
        </>
      )}
    </button>
  );

  return (
    <>
      {/* `Menu`, not `Popover`: a Popover is an absolutely-positioned child of
          the sidebar, so the collapsed rail clipped it away entirely. `Menu`
          portals to the body, matching how the design offsets a fixed panel from
          the sidebar's edge. */}
      <Menu
        open={open}
        onOpenChange={setOpen}
        side="right"
        align="start"
        aria-label="Switch organisation"
        contentClassName="w-[248px] ring-offset-v2-sidebar"
        trigger={collapsed ? <Tooltip label={name} side="right">{triggerButton}</Tooltip> : triggerButton}
      >
        <div
          className="-mx-1 -mt-1 mb-1 border-b border-v2-border p-1.5"
          // The menu runs typeahead on keystrokes; without this the search field
          // would lose every character to it.
          onKeyDown={event => event.stopPropagation()}
        >
            <Input
              type="search"
              value={query}
              onChange={setQuery}
              aria-label="Search organisations"
              placeholder="Search organisations…"
              leftIcon={<Search size={13} />}
              classNames={{ field: 'h-7 rounded-v2-md', input: 'text-v2-sm' }}
            />
        </div>

        <div className="max-h-[280px] overflow-y-auto">
          {visibleMemberships.length === 0 ? (
            // Without this a query that matches nothing leaves a blank panel
            // with "Add organisation" floating under it, which reads as broken
            // rather than filtered.
            <p className="px-2 py-3 text-center text-v2-xs text-v2-muted-foreground">
              No organisations match your search
            </p>
          ) : null}
          {visibleMemberships.map(({ organization: org }) => {
            const isActive = org.id === organization?.id;
            return (
              <MenuItem
                key={org.id}
                label={org.name}
                onClick={() => {
                  if (!isActive) switchOrg(org.id);
                }}
              >
                <OrgMark imageUrl={org.imageUrl} />
                <span className="min-w-0 flex-1 truncate">{org.name}</span>
                {isActive ? <Check size={14} className="ml-auto shrink-0" /> : null}
              </MenuItem>
            );
          })}
        </div>

        {canCreateOrg ? (
          <>
            <MenuSeparator />
            <MenuItem label="Add organisation" onClick={() => setIsAddAccountOpen(true)}>
              <span aria-hidden className="grid size-5 shrink-0 place-items-center">
                <Plus size={14} />
              </span>
              Add organisation
            </MenuItem>
          </>
        ) : null}
      </Menu>

      <AddAccountModal open={isAddAccountOpen} onOpenChange={setIsAddAccountOpen} />
    </>
  );
}
